import React, { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import { useModal } from "../../context/ModalContext";

export default function NotificationsTab() {
  const modal = useModal();
  const [settings, setSettings] = useState({ email: true, push: true });

  useEffect(() => {
    api
      .get("/api/settings/notifications")
      .then(res => setSettings(res.data))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    setSettings(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleSave = () => {
    api
      .put("/api/settings/notifications", settings)
      .then(() => modal.alert("Preferences saved successfully!", "success"))
      .catch(err => console.error(err));
  };

  const toggles = [
    { name: "email", label: "Email Notifications", desc: "Receive updates via email" },
    { name: "push", label: "Push Notifications", desc: "Browser push notifications" },
  ];

  // Group toggles by channel/topic for CRO-friendly 2-col layout.
  // Heuristics: campaign/outreach/reply/lead -> Engagement; billing/invoice/subscription/limit/expired/error -> Billing & System.
  const engagementKeywords = /campaign|outreach|reply|lead|message|chat|inbox|push/i;
  const billingKeywords = /billing|invoice|subscription|limit|expired|error|system|security|account/i;

  const engagementToggles = toggles.filter(
    (t) => engagementKeywords.test(t.name) || engagementKeywords.test(t.label) || engagementKeywords.test(t.desc)
  );
  const billingToggles = toggles.filter(
    (t) => billingKeywords.test(t.name) || billingKeywords.test(t.label) || billingKeywords.test(t.desc)
  );
  const groupedNames = new Set([...engagementToggles, ...billingToggles].map((t) => t.name));
  const otherToggles = toggles.filter((t) => !groupedNames.has(t.name));

  // Distribute uncategorized toggles into the smaller column for balance.
  const leftColumn = [...engagementToggles];
  const rightColumn = [...billingToggles];
  otherToggles.forEach((t) => {
    if (leftColumn.length <= rightColumn.length) leftColumn.push(t);
    else rightColumn.push(t);
  });

  const renderToggle = (t) => (
    <label
      key={t.name}
      className="flex items-center justify-between p-3 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors cursor-pointer"
    >
      <div>
        <div className="text-sm font-medium text-ink-primary">{t.label}</div>
        <div className="text-xs text-ink-muted">{t.desc}</div>
      </div>
      <input
        type="checkbox"
        name={t.name}
        checked={settings[t.name]}
        onChange={handleChange}
        className="w-4 h-4 rounded border-surface-border text-brand accent-brand"
      />
    </label>
  );

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-1">Notification Preferences</h2>
      <p className="text-sm text-ink-muted mb-5">
        Choose how you want to hear from Walytic. Group by topic to keep the noise down.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-surface-border bg-surface-card p-4">
          <header className="mb-3">
            <h3 className="text-sm font-semibold text-ink-primary">Engagement & Activity</h3>
            <p className="text-xs text-ink-muted">Campaigns, replies, leads, and inbox updates.</p>
          </header>
          <div className="space-y-3">
            {leftColumn.length > 0 ? (
              leftColumn.map(renderToggle)
            ) : (
              <p className="text-xs text-ink-muted italic">No engagement notifications configured.</p>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-surface-border bg-surface-card p-4">
          <header className="mb-3">
            <h3 className="text-sm font-semibold text-ink-primary">Billing & System</h3>
            <p className="text-xs text-ink-muted">Invoices, limits, security, and account alerts.</p>
          </header>
          <div className="space-y-3">
            {rightColumn.length > 0 ? (
              rightColumn.map(renderToggle)
            ) : (
              <p className="text-xs text-ink-muted italic">No billing or system notifications configured.</p>
            )}
          </div>
        </section>
      </div>

      <button className="btn-primary mt-5" onClick={handleSave}>
        Save Preferences
      </button>
    </div>
  );
}
